import React, {useState, useRef} from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import MobileFriendlyIcon from "@mui/icons-material/MobileFriendly";
import {apiHelper, baseUrl} from "helpers/util";
import {notify} from "helpers/globalFunctions";
import ColumnBox from "components/ColumnBox";
import RowBox from "components/RowBox";
import SmallHeading from "components/SmallHeading";
import html2canvas from "html2canvas";
import {Alert, Snackbar} from "@mui/material";
import {message, Modal} from "antd";
import {PrinterService} from "./components/PrinterService";
import {SlipGenerator} from "./components/SlipGenerator";
import RecordDisplay from "./components/RecordDisplay";
import {useNavigate} from "react-router-dom";

export default function EditableSundayAttendance() {
    console.log("EditableSundayAttendance is rendering");
    const navigate = useNavigate();

    const [mobileNo, setMobileNo] = useState("");
    const [getRecordId, setRecordId] = useState({});
    const [getDuplicateAttendance, setDuplicateAttendance] = useState({});
    const [loading, setLoading] = useState(false);
    const [areOptionsLoading, setAreOptionsLoading] = useState(false);
    const [isFormSubmitting, setIsFormSubmitting] = useState(false);
    const [isPrinting, setIsPrinting] = useState(false);
    const [fetchedRecord, setFetchedRecord] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [errorColor, setErrorColor] = useState("");

    const formErrorState = useRef({});
    const printerService = useRef(new PrinterService());
    const slipGenerator = useRef(new SlipGenerator());

    // Use slip generator methods
    const getDateStr = () => slipGenerator.current.getDateStr();
    const getTimeStr = () => slipGenerator.current.getTimeStr();

    // State for editing
    const [editState, setEditState] = useState({
        name: false,
        mobileNo: false,
        folkLevel: false,
        folkGuide: false,
        occupation: false,
        folkCentre: false,
        areaOfStay: false,
        chantingRounds: false,
        ashrayaLevel: false,
        courseMaterial: false,
        paymentScreenshot: false,
        paymentAmount: false,
    });
    const [editedRecord, setEditedRecord] = useState({
        name: "",
        mobileNo: "",
        folkLevel: "",
        folkGuide: "",
        occupation: "",
        folkCentre: "",
        areaOfStay: "",
        chantingRounds: "",
        ashrayaLevel: "",
        courseMaterial: "",
        paymentScreenshot: null,
        paymentAmount: "",
    });

    const resetForm = () => {
        try {
            // Reset all form states
            setMobileNo("");
            setFetchedRecord(null);
            setErrorMessage(null);
            setErrorColor("");
            setRecordId({});
            setDuplicateAttendance({});

            // Reset edit states
            setEditState({
                name: false,
                mobileNo: false,
                folkLevel: false,
                folkGuide: false,
                occupation: false,
                folkCentre: false,
                areaOfStay: false,
                chantingRounds: false,
                ashrayaLevel: false,
                courseMaterial: false,
                paymentScreenshot: false,
                paymentAmount: false,
            });

            // Reset edited record
            setEditedRecord({
                name: "",
                mobileNo: "",
                folkLevel: "",
                folkGuide: "",
                occupation: "",
                folkCentre: "",
                areaOfStay: "",
                chantingRounds: "",
                ashrayaLevel: "",
                courseMaterial: "",
                paymentScreenshot: null,
                paymentAmount: "",
            });

            // Clear form error state
            formErrorState.current = {};

            // Clear DOM elements safely
            const printElement = document.getElementById("print-content");
            if (printElement) {
                printElement.innerHTML = "";
            }

            console.log("Form reset completed successfully");
        } catch (error) {
            console.error("Error in resetForm:", error);
        }
    };

    const resetDom = () => {
        try {
            setFetchedRecord(null);
            setErrorMessage(null);
            setErrorColor("");

            // Reset edit states and edited record to prevent stale data
            setEditState({
                name: false,
                mobileNo: false,
                folkLevel: false,
                folkGuide: false,
                occupation: false,
                folkCentre: false,
                areaOfStay: false,
                chantingRounds: false,
                ashrayaLevel: false,
                courseMaterial: false,
                paymentScreenshot: false,
                paymentAmount: false,
            });

            setEditedRecord({
                name: "",
                mobileNo: "",
                folkLevel: "",
                folkGuide: "",
                occupation: "",
                folkCentre: "",
                areaOfStay: "",
                chantingRounds: "",
                ashrayaLevel: "",
                courseMaterial: "",
                paymentScreenshot: null,
                paymentAmount: "",
            });

            slipGenerator.current.hardResetSlipDOM();
            console.log("DOM reset completed successfully");
        } catch (error) {
            console.error("Error in resetDom:", error);
        }
    };

    const searchByMobileNo = async (mobileNo) => {
        try {
            setAreOptionsLoading(true);
            setErrorMessage(null);
            resetDom();

            // Log the request details for debugging
            console.log("Making API request with mobile:", mobileNo);
            console.log("Current Date String:", getDateStr());
            console.log("Current Time String:", getTimeStr());

            const response = await apiHelper.get({
                url: `${baseUrl}/.netlify/functions/editable_sunday_attendance`,
                params: {search_text: mobileNo},
            });

            // Log the response for debugging
            console.debug("API Response:", JSON.stringify(response));

            if (response.status) {
                if (!response.json) {
                    throw new Error("Response json is undefined");
                }
                if (response.json.length === 0) {
                    setErrorMessage("No Records found for this Mobile Number.");
                    setErrorColor("error"); // Material-UI color for error messages
                    return; // Early exit if no records found
                }
                setRecordId(response.json[0]["Record ID"]);
                setDuplicateAttendance(response.json[0]["Today attended ?"]);
                setFetchedRecord(response.json[0]);
            } else {
                setErrorMessage(`API returned status false: ${JSON.stringify(response.json)}`);
                setErrorColor("error");
            }
        } catch (error) {
            // Detailed error logging
            console.error("Error in searchByMobileNo:", {
                message: error.message,
                stack: error.stack,
                line: error.lineNumber,
                fileName: error.fileName,
            });
            setErrorMessage("An error occurred while searching.");
            setErrorColor("error");
        } finally {
            setAreOptionsLoading(false);
        }
    };

    const checkForFormErrors = (field, isError) => {
        formErrorState.current[field] = isError;
        return isError;
    };

    const connectToBluetooth = () => {
        printerService.current.connectToBluetooth();
    };

    // All printing logic moved to PrinterService class

    const onClickPrintSlip = () => {
        if (isPrinting) {
            notify.warning({description: "Printing in progress. Please wait..."});
            return;
        }

        const level = fetchedRecord["FOLK Level (Display)"];
        const count = Number(fetchedRecord["Count"]);
        const restrictedLevels = ["GFY Registered", "AOMC Attended", "GFY Attended"];
        const isRestricted = (restrictedLevels.includes(level) && count >= 2) || (!level && count >= 2);

        if (isRestricted) {
            message.warning("FOLK Divisional Head approval is required to mark attendance and print the slip.", 5);
            setTimeout(() => {
                Modal.confirm({
                    title: "Approval Needed",
                    content: "Do you have FOLK Divisional Head approval to continue?",
                    okText: "Yes, continue",
                    cancelText: "No",
                    onOk: () => handlePrintSlip(fetchedRecord, false),
                    onCancel: () => message.error("Approval denied. Cannot proceed."),
                });
            }, 5000);
            return;
        }

        //Printer connection check
        if (!window.AndroidApp && !printerService.current.isConnected()) {
            notify.error({description: "Connect to Printer first"});
            return;
        }

        if (Object.values(formErrorState.current).some((isError) => isError)) {
            notify.error({description: "Please fill all details"});
            return;
        }

        handlePrintSlip(fetchedRecord, getDuplicateAttendance === 1);
    };

    const clearAllPrintHistory = async () => {
        try {
            console.log("Starting complete print history cleanup");

            // Set loading state to prevent new operations
            setIsPrinting(true);

            // Force cleanup printer service first
            await printerService.current.forceCleanup();

            // Wait for printer cleanup to complete
            await new Promise((resolve) => setTimeout(resolve, 500));

            // Clear DOM with comprehensive cleanup
            slipGenerator.current.hardResetSlipDOM();

            // Additional DOM cleanup
            const printElement = document.getElementById("print-content");
            if (printElement) {
                printElement.innerHTML = "";
                printElement.style.cssText = "";
            }

            // Clear any lingering print state
            const bodyElement = document.querySelector("body");
            if (bodyElement) {
                bodyElement.classList.remove("print");
            }

            // Force garbage collection if available
            if (window.gc) {
                window.gc();
            }

            console.log("Complete print history cleanup finished");
        } catch (error) {
            console.error("Error in clearAllPrintHistory:", error);
        } finally {
            // Always reset printing state
            setIsPrinting(false);
        }
    };

    // Enhanced cleanup after record updates
    const cleanupAfterUpdate = async () => {
        try {
            console.log("Starting cleanup after record update");

            // Clear all print-related state
            await clearAllPrintHistory();

            // Additional delay to ensure cleanup completion
            await new Promise((resolve) => setTimeout(resolve, 300));

            console.log("Cleanup after record update completed");
        } catch (error) {
            console.error("Error in cleanupAfterUpdate:", error);
        }
    };

    const completeCleanupAfterPrint = async () => {
        try {
            console.log("Starting complete cleanup after print");

            // 1. Clear printer service state
            await printerService.current.forceCleanup();

            // 2. Reset slip DOM completely
            slipGenerator.current.hardResetSlipDOM();

            // 3. Reset slip structure to original state
            const slip = document.getElementById("print-slip");
            if (slip) {
                slip.innerHTML = `
                    <div style="display: flex; justify-content: center; width: 100%;">
                        <img src="/images/folklogo.png" alt="FOLK Logo" style="width: 45mm; display: block; margin: 0 auto;" />
                    </div>
                    <div style="width: 100%; text-align: center; margin-top: 5mm; margin-bottom: 5mm;">
                        <span style="display: inline-block; font-size: 6.2mm; font-weight: 700; letter-spacing: 0.10em; text-align: center;">
                        FOLK SUNDAY MEET-UP ATTENDANCE
                        </span>
                    </div>
                    <div id="print-content" style="display: flex; flex-direction: column; justify-content: flex-start; align-items: center; background: white; font-size: 5.5mm; letter-spacing: 0.15em; width: 100%;"></div>
                `;
            }

            // 4. Remove all canvases and print artifacts
            document.querySelectorAll("canvas").forEach((c) => {
                try {
                    c.width = 0;
                    c.height = 0;
                    if (c.parentNode) c.parentNode.removeChild(c);
                } catch {}
            });

            // 5. Remove print classes
            document.querySelector("body")?.classList.remove("print");
            document.getElementById("print-slip")?.classList.remove("print");

            // 6. Force garbage collection
            if (window.gc) window.gc();

            console.log("Complete cleanup after print finished");
        } catch (error) {
            console.error("Complete cleanup failed:", error);
        }
    };

    const handlePrintSlip = async (fetchedRecord, isDuplicate) => {
        if (isPrinting || printerService.current.isBusy()) {
            notify.warning({description: "Previous print job still in progress. Please wait..."});
            return;
        }

        try {
            setIsPrinting(true);
            console.log("Starting print operation");

            // Complete cleanup before starting
            await completeCleanupAfterPrint();
            await new Promise((resolve) => setTimeout(resolve, 300));

            // Generate slip content
            const slipContent = slipGenerator.current.generateSlipContent(fetchedRecord, isDuplicate);

            // Update DOM with proper timing
            slipGenerator.current.updateSlipDOM(slipContent);
            await new Promise((resolve) => setTimeout(resolve, 600)); // Wait for DOM update

            if (window.AndroidApp) {
                // Android printing
                const printElement = document.getElementById("print-slip");
                if (printElement) {
                    void printElement.offsetHeight;

                    const canvas = await html2canvas(printElement, {
                        width: printElement.offsetWidth,
                        height: printElement.offsetHeight + 100,
                        scale: 1,
                        backgroundColor: "#ffffff",
                        logging: false,
                    });

                    const imageDataUrl = canvas.toDataURL("image/jpeg").split(";base64,")[1];
                    window.AndroidApp.printPrasadam(imageDataUrl);
                }
            } else {
                // Try SHREYANS printer first, fallback to regular Bluetooth printing
                const shreyansSuccess = await slipGenerator.current.printToShreyansDevice(slipContent);
                if (!shreyansSuccess) {
                    await printerService.current.printSlip("print-slip", slipGenerator.current);
                }
            }

            updatePrintedStatus(getRecordId);
        } catch (error) {
            console.error("Print error:", error);
            notify.error({description: "Printing failed. Please try again."});
        } finally {
            // Single cleanup after everything is done
            setTimeout(async () => {
                await completeCleanupAfterPrint();
                setIsPrinting(false);
                setIsFormSubmitting(false);
                resetForm();
                console.log("Print operation completed and cleaned up");
            }, 1500);
        }
    };

    const updatePrintedStatus = (recordId) => {
        console.log("updatePrintedStatus get called...");
        console.log("Record ID: ", recordId);
        // Check if the attendance has already been marked
        if (getDuplicateAttendance === 1) {
            console.log("Attendance is already marked. Skipping update.");
            setIsFormSubmitting(false);
            resetForm();
        } else {
            const payload = {
                record_id: recordId,
            };

            console.log("Payload: ", payload);
            setIsFormSubmitting(true);
            apiHelper
            .post({
                url: `${baseUrl}/.netlify/functions/marked_attendance`,
                data: payload,
            })
            .then((response) => {
                if (response.status) {
                    setIsFormSubmitting(false);
                    resetForm();
                    console.log("Sunday attended updated successfully");
                } else {
                    console.error("Error updating 'Sunday attended' column:", response);
                }
            })
            .catch((error) => {
                console.error("Error updating 'Sunday attended' column:", error);
            });
        }
    };

    const handleEdit = (field) => {
        setEditState((prevState) => ({...prevState, [field]: !prevState[field]}));

        setEditedRecord((prev) => ({
            ...prev,
            name: prev.name || fetchedRecord["HK Japa User Name"] || fetchedRecord["Name"] || "",
            mobileNo: prev.mobileNo || mobileNo || fetchedRecord["Mobile No."] || "",
            folkLevel: prev.folkLevel || fetchedRecord["FOLK Level (Display)"] || "",
            ashrayaLevel: prev.ashrayaLevel || fetchedRecord["Current Ashraya Level"] || "",
            courseMaterial: prev.courseMaterial || fetchedRecord["Course Material"] || "",
            occupation: prev.occupation || fetchedRecord["Occupation"] || "",
            folkCentre: prev.folkCentre || fetchedRecord["FOLK Centre"] || "",
            areaOfStay: prev.areaOfStay || fetchedRecord["Area of Stay"] || "",
            chantingRounds: prev.chantingRounds || fetchedRecord["Chanting"] || "",
            paymentAmount: prev.paymentAmount || fetchedRecord["GFY Payment"] || "",
            paymentScreenshot: prev.paymentScreenshot || null,
        }));
    };

    const handleChange = (e) => {
        const {name, value, files} = e.target;
        console.log("handleChange called:", {name, value, files, type: typeof value});

        setEditedRecord((prev) => {
            let fieldValue = value;

            if (name === "paymentScreenshot") {
                // Handle File object from direct file input
                if (files && files[0]) {
                    fieldValue = files[0];
                }
                // Handle base64 data from RecordDisplay component
                else if (value && typeof value === "object" && value.base64) {
                    fieldValue = value;
                }
            }

            const updated = {
                ...prev,
                [name]: fieldValue,
            };
            console.log("Updated editedRecord:", updated);
            return updated;
        });
    };

    const handleUpdate = () => {
        console.log("Record Id: ", getRecordId);
        setLoading(true);

        const currentFolkLevel = editedRecord.folkLevel || fetchedRecord["FOLK Level (Display)"];
        const isGFYLevel = ["GFY Attended", "GFY Registered", "AOMC Attended"].includes(currentFolkLevel);

        // Check if file upload is needed
        const hasFileUpload =
            editState.paymentScreenshot &&
            editedRecord.paymentScreenshot &&
            (editedRecord.paymentScreenshot instanceof File ||
                (typeof editedRecord.paymentScreenshot === "object" && editedRecord.paymentScreenshot.base64));

        if (hasFileUpload) {
            // Use FormData for file upload
            const formData = new FormData();
            formData.append("recordId", getRecordId);

            // Add edited fields
            if (editState.name)
                formData.append(
                    "name",
                    editedRecord.name || fetchedRecord["HK Japa User Name"] || fetchedRecord["Name"] || ""
                );
            if (editState.folkLevel)
                formData.append("folkLevel", editedRecord.folkLevel || fetchedRecord["FOLK Level (Display)"] || "");

            if (isGFYLevel) {
                if (editState.ashrayaLevel)
                    formData.append(
                        "ashrayaLevel",
                        editedRecord.ashrayaLevel || fetchedRecord["Current Ashraya Level"] || ""
                    );
                if (editState.courseMaterial)
                    formData.append(
                        "courseMaterial",
                        editedRecord.courseMaterial || fetchedRecord["Course Material"] || ""
                    );
                if (editState.occupation)
                    formData.append("occupation", editedRecord.occupation || fetchedRecord["Occupation"] || "");
                if (editState.folkCentre)
                    formData.append("folkCentre", editedRecord.folkCentre || fetchedRecord["FOLK Centre"] || "");
                if (editState.areaOfStay)
                    formData.append("areaOfStay", editedRecord.areaOfStay || fetchedRecord["Area of Stay"] || "");
                if (editState.chantingRounds)
                    formData.append(
                        "chantingRounds",
                        editedRecord.chantingRounds?.toString() || fetchedRecord["Chanting"]?.toString() || ""
                    );
                if (editState.paymentAmount)
                    formData.append("paymentAmount", editedRecord.paymentAmount || fetchedRecord["GFY Payment"] || "");
            } else {
                formData.append(
                    "ashrayaLevel",
                    editedRecord.ashrayaLevel || fetchedRecord["Current Ashraya Level"] || ""
                );
                formData.append(
                    "courseMaterial",
                    editedRecord.courseMaterial || fetchedRecord["Course Material"] || ""
                );
                formData.append("occupation", editedRecord.occupation || fetchedRecord["Occupation"] || "");
                formData.append("folkCentre", editedRecord.folkCentre || fetchedRecord["FOLK Centre"] || "");
                formData.append("areaOfStay", editedRecord.areaOfStay || fetchedRecord["Area of Stay"] || "");
                formData.append(
                    "chantingRounds",
                    editedRecord.chantingRounds?.toString() || fetchedRecord["Chanting"]?.toString() || ""
                );
            }

            // Add file
            if (editedRecord.paymentScreenshot instanceof File) {
                formData.append("paymentScreenshot", editedRecord.paymentScreenshot);
            } else if (editedRecord.paymentScreenshot.base64) {
                // Convert base64 to blob for FormData
                const base64Data = editedRecord.paymentScreenshot.base64.split(",")[1];
                const byteCharacters = atob(base64Data);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], {type: editedRecord.paymentScreenshot.type});
                formData.append("paymentScreenshot", blob, editedRecord.paymentScreenshot.filename);
            }

            console.log("Uploading with FormData");

            // File upload request
            fetch(`${baseUrl}/.netlify/functions/editable_sunday_attendance`, {
                method: "PATCH",
                body: formData,
            })
            .then((response) => response.json())
            .then(async (response) => {
                if (response.status !== false && response.records) {
                    notify.success({description: "Record updated successfully!"});
                    await cleanupAfterUpdate();
                    setFetchedRecord(response.records[0].fields);
                    resetEditStates();
                } else {
                    console.error("Error updating record:", response);
                    notify.error({description: "Failed to update record. Please try again."});
                }
                setLoading(false);
            })
            .catch((error) => {
                console.error("Error updating record:", error);
                notify.error({description: "Failed to update record. Please try again."});
                setLoading(false);
            });
        } else {
            // Regular JSON update
            const payload = {recordId: getRecordId};

            if (editState.name)
                payload.name = editedRecord.name || fetchedRecord["HK Japa User Name"] || fetchedRecord["Name"] || "";
            if (editState.folkLevel)
                payload.folkLevel = editedRecord.folkLevel || fetchedRecord["FOLK Level (Display)"] || "";

            if (isGFYLevel) {
                if (editState.ashrayaLevel)
                    payload.ashrayaLevel = editedRecord.ashrayaLevel || fetchedRecord["Current Ashraya Level"] || "";
                if (editState.courseMaterial)
                    payload.courseMaterial = editedRecord.courseMaterial || fetchedRecord["Course Material"] || "";
                if (editState.occupation)
                    payload.occupation = editedRecord.occupation || fetchedRecord["Occupation"] || "";
                if (editState.folkCentre)
                    payload.folkCentre = editedRecord.folkCentre || fetchedRecord["FOLK Centre"] || "";
                if (editState.areaOfStay)
                    payload.areaOfStay = editedRecord.areaOfStay || fetchedRecord["Area of Stay"] || "";
                if (editState.chantingRounds)
                    payload.chantingRounds =
                        editedRecord.chantingRounds?.toString() || fetchedRecord["Chanting"]?.toString() || "";
                if (editState.paymentAmount)
                    payload.paymentAmount = editedRecord.paymentAmount || fetchedRecord["GFY Payment"] || "";

                // Always include paymentAmount if it exists in editedRecord
                if (editedRecord.paymentAmount) payload.paymentAmount = editedRecord.paymentAmount;
            } else {
                payload.ashrayaLevel = editedRecord.ashrayaLevel || fetchedRecord["Current Ashraya Level"] || "";
                payload.courseMaterial = editedRecord.courseMaterial || fetchedRecord["Course Material"] || "";
                payload.occupation = editedRecord.occupation || fetchedRecord["Occupation"] || "";
                payload.folkCentre = editedRecord.folkCentre || fetchedRecord["FOLK Centre"] || "";
                payload.areaOfStay = editedRecord.areaOfStay || fetchedRecord["Area of Stay"] || "";
                payload.chantingRounds =
                    editedRecord.chantingRounds?.toString() || fetchedRecord["Chanting"]?.toString() || "";
            }

            console.log("Updating record with payload:", payload);

            apiHelper
            .patch({
                url: `${baseUrl}/.netlify/functions/editable_sunday_attendance`,
                data: payload,
            })
            .then(async (response) => {
                if (response.status && !response.json?.error) {
                    const updatedFields = response.json?.records?.[0]?.fields;

                    console.log("Updated fields from Airtable:", updatedFields);
                    notify.success({description: "Record updated successfully!"});

                    await cleanupAfterUpdate();
                    setFetchedRecord(response.json);
                    resetEditStates();
                } else {
                    console.error("Error updating record:", response);
                    notify.error({description: "Failed to update record. Please try again."});
                }
                setLoading(false);
            })
            .catch((error) => {
                console.error("Error updating record:", error);
                notify.error({description: "Failed to update record. Please try again."});
                setLoading(false);
            });
        }
    };

    const resetEditStates = () => {
        setEditState({
            name: false,
            mobileNo: false,
            folkLevel: false,
            folkGuide: false,
            occupation: false,
            folkCentre: false,
            chantingRounds: false,
            ashrayaLevel: false,
            courseMaterial: false,
            areaOfStay: false,
            paymentAmount: false,
            paymentScreenshot: false,
        });
        setEditedRecord({});
    };

    return (
        <Box sx={{display: "flex", flexDirection: "row", justifyContent: "center", padding: "15px"}}>
            <ColumnBox>
                <SmallHeading sx={{alignSelf: "center"}}>FOLK Sunday Program</SmallHeading>
                <ColumnBox sx={{alignItems: "center", maxWidth: "500px", width: "100%"}}>
                    {/* Row for TextField, Circular Progress, and Button */}
                    <RowBox
                        sx={{
                            width: "100%",
                            marginTop: "15px",
                            alignItems: "center", // Align vertically
                            justifyContent: "space-between", // Space out TextField and Button
                        }}
                    >
                        {/* Container for TextField and Spinner */}
                        <Box sx={{width: "85%", display: "flex", alignItems: "center"}}>
                            <TextField
                                required
                                label="Mobile No."
                                autoFocus
                                size="small"
                                value={mobileNo}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setMobileNo(val);
                                    checkForFormErrors("mobileNo", !val);
                                }}
                                sx={{flexGrow: 1, marginLeft: "30px"}} // Take up available space
                            />

                            {/* Circular Progress or Mobile Icon beside TextField */}
                            <Box sx={{marginLeft: "10px"}}>
                                {areOptionsLoading ? (
                                    <CircularProgress size="1.5rem" />
                                ) : (
                                    <MobileFriendlyIcon size="1.5rem" />
                                )}
                            </Box>
                        </Box>
                    </RowBox>

                    {/* Search Button */}
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => searchByMobileNo(mobileNo)}
                        disabled={!mobileNo || areOptionsLoading}
                        sx={{
                            minWidth: "120px",
                            marginTop: "15px", // Space between the input and button
                        }}
                    >
                        Search
                    </Button>

                    {!window.AndroidApp && (
                        <Button sx={{marginTop: "15px"}} onClick={() => connectToBluetooth()} variant="outlined">
                            Connect Printer
                        </Button>
                    )}

                    {/* Attendance Slip Section */}

                    <div
                        id="print-slip"
                        style={{
                            textAlign: "center",
                            marginTop: "20px",
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                width: "100%",
                                maxWidth: "400px",
                            }}
                        >
                            <img
                                src="/images/folklogo.png"
                                alt="FOLK Logo"
                                style={{width: "45mm", display: "block", margin: "0 auto"}}
                            />
                        </div>
                        <div
                            style={{
                                width: "100%",
                                maxWidth: "400px",
                                textAlign: "center",
                                marginTop: "5mm",
                                marginBottom: "5mm",
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            <span
                                style={{
                                    display: "inline-block",
                                    fontSize: "6.2mm",
                                    fontWeight: 700,
                                    letterSpacing: "0.10em",
                                    textTransform: "uppercase",
                                    color: "#000",
                                    textAlign: "center",
                                }}
                            >
                                FOLK SUNDAY MEET-UP ATTENDANCE
                            </span>
                        </div>
                        <div
                            id="print-content"
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "flex-start",
                                alignItems: "center",
                                background: "white",
                                fontSize: "5.5mm",
                                letterSpacing: "0.15em",
                                width: "100%",
                                maxWidth: "400px",
                            }}
                        ></div>
                    </div>
                    <hr style={{width: "100%", border: "none", borderTop: "1px solid #000", margin: "4mm 0"}} />

                    {errorMessage && (
                        <Snackbar
                            open={!!errorMessage}
                            autoHideDuration={8000}
                            onClose={() => setErrorMessage(null)}
                            anchorOrigin={{vertical: "bottom", horizontal: "center"}} // Center the Snackbar
                        >
                            <Alert severity={errorColor} onClose={() => setErrorMessage(null)} sx={{width: "100%"}}>
                                {errorMessage}
                            </Alert>
                        </Snackbar>
                    )}

                    <RecordDisplay
                        fetchedRecord={fetchedRecord}
                        editState={editState}
                        editedRecord={editedRecord}
                        loading={loading}
                        onEdit={handleEdit}
                        onChange={handleChange}
                        onUpdate={handleUpdate}
                        getDateStr={getDateStr}
                        getTimeStr={getTimeStr}
                    />
                    <RowBox
                        sx={{width: "100%", marginTop: "15px", alignItems: "center", justifyContent: "space-around"}}
                    >
                        <Button onClick={onClickPrintSlip} variant="outlined" disabled={isPrinting}>
                            {isPrinting ? "Printing..." : "Print Slip"}
                        </Button>
                    </RowBox>
                </ColumnBox>
            </ColumnBox>
        </Box>
    );
}
